import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import {
  getPageTitleMessageId,
  PAGE_TITLE_SITE_SUFFIX,
} from "@app/utils/pageTitleConfig";

const messages = defineMessages({
  pageTitleHome: { id: "pageTitleHome", defaultMessage: "Home" },
  pageTitleLogIn: { id: "pageTitleLogIn", defaultMessage: "Log in" },
  pageTitleSignUp: { id: "pageTitleSignUp", defaultMessage: "Sign up" },
  pageTitleActivation: {
    id: "pageTitleActivation",
    defaultMessage: "Activation",
  },
  pageTitleResetPassword: {
    id: "pageTitleResetPassword",
    defaultMessage: "Reset password",
  },
  pageTitleResetPasswordError: {
    id: "pageTitleResetPasswordError",
    defaultMessage: "Reset password error",
  },
  pageTitleSetPassword: {
    id: "pageTitleSetPassword",
    defaultMessage: "Set password",
  },
  pageTitleSendEmail: {
    id: "pageTitleSendEmail",
    defaultMessage: "Send email",
  },
  pageTitleUnlock: { id: "pageTitleUnlock", defaultMessage: "Unlock account" },
  pageTitleUpdateEmailError: {
    id: "pageTitleUpdateEmailError",
    defaultMessage: "Update email error",
  },
  pageTitleMyAccount: {
    id: "pageTitleMyAccount",
    defaultMessage: "My Account",
  },
  pageTitleConfirmation: {
    id: "pageTitleConfirmation",
    defaultMessage: "Confirmation",
  },
  pageTitleSetupProfile: {
    id: "pageTitleSetupProfile",
    defaultMessage: "Setup profile",
  },
  pageTitleSecuritySettings: {
    id: "pageTitleSecuritySettings",
    defaultMessage: "Security settings",
  },
  pageTitleChangePassword: {
    id: "pageTitleChangePassword",
    defaultMessage: "Change password",
  },
  pageTitleEditMfa: {
    id: "pageTitleEditMfa",
    defaultMessage: "Edit two-factor authentication",
  },
  pageTitleUpdateEmail: {
    id: "pageTitleUpdateEmail",
    defaultMessage: "Update email",
  },
  pageTitleResendUpdateEmail: {
    id: "pageTitleResendUpdateEmail",
    defaultMessage: "Resend update email",
  },
  pageTitleContactDetails: {
    id: "pageTitleContactDetails",
    defaultMessage: "Contact details",
  },
  pageTitlePersonalInformation: {
    id: "pageTitlePersonalInformation",
    defaultMessage: "Personal information",
  },
  pageTitleCommunicationPreferences: {
    id: "pageTitleCommunicationPreferences",
    defaultMessage: "Communication preferences",
  },
  pageTitleVideo: { id: "pageTitleVideo", defaultMessage: "Video" },
  pageTitleTest: { id: "pageTitleTest", defaultMessage: "Test" },
  pageTitleTestHistory: {
    id: "pageTitleTestHistory",
    defaultMessage: "Test History",
  },
  pageTitleSampleDraw: {
    id: "pageTitleSampleDraw",
    defaultMessage: "Sample draw",
  },
  pageTitleSimpleOrder: {
    id: "pageTitleSimpleOrder",
    defaultMessage: "Order",
  },
  pageTitleScheduleBloodDraw: {
    id: "pageTitleScheduleBloodDraw",
    defaultMessage: "Schedule blood draw",
  },
  pageTitleDrawRequest: {
    id: "pageTitleDrawRequest",
    defaultMessage: "Draw request",
  },
  pageTitleBillingCenter: {
    id: "pageTitleBillingCenter",
    defaultMessage: "Billing Center",
  },
  pageTitleBillingGuestLogin: {
    id: "pageTitleBillingGuestLogin",
    defaultMessage: "Billing guest login",
  },
  pageTitlePayment: { id: "pageTitlePayment", defaultMessage: "Payment" },
  pageTitleReviewBills: {
    id: "pageTitleReviewBills",
    defaultMessage: "Review bills",
  },
  pageTitlePayBill: { id: "pageTitlePayBill", defaultMessage: "Pay My Bill" },
  pageTitleBillingFailure: {
    id: "pageTitleBillingFailure",
    defaultMessage: "Billing failure",
  },
  pageTitlePaymentConfirmation: {
    id: "pageTitlePaymentConfirmation",
    defaultMessage: "Payment confirmation",
  },
  pageTitlePaymentPlanDetail: {
    id: "pageTitlePaymentPlanDetail",
    defaultMessage: "Payment plan detail",
  },
  pageTitlePaymentReceipt: {
    id: "pageTitlePaymentReceipt",
    defaultMessage: "Payment receipt",
  },
  pageTitlePaymentSuccess: {
    id: "pageTitlePaymentSuccess",
    defaultMessage: "Payment success",
  },
  pageTitleUpdatePayment: {
    id: "pageTitleUpdatePayment",
    defaultMessage: "Update payment",
  },
  pageTitleBillingHistory: {
    id: "pageTitleBillingHistory",
    defaultMessage: "Billing history",
  },
  pageTitleBillingHelp: {
    id: "pageTitleBillingHelp",
    defaultMessage: "Billing help",
  },
  pageTitleStatements: {
    id: "pageTitleStatements",
    defaultMessage: "Statements",
  },
  pageTitlePaymentPlans: {
    id: "pageTitlePaymentPlans",
    defaultMessage: "Payment plans",
  },
  pageTitlePaymentHistory: {
    id: "pageTitlePaymentHistory",
    defaultMessage: "Payment history",
  },
  pageTitlePatientEducation: {
    id: "pageTitlePatientEducation",
    defaultMessage: "Patient education",
  },
  pageTitleBillingUpdates: {
    id: "pageTitleBillingUpdates",
    defaultMessage: "Billing updates",
  },
  pageTitleBillingEstimate: {
    id: "pageTitleBillingEstimate",
    defaultMessage: "Billing estimate",
  },
  pageTitleGuestLogin: {
    id: "pageTitleGuestLogin",
    defaultMessage: "Guest login",
  },
  pageTitleNonCoveredService: {
    id: "pageTitleNonCoveredService",
    defaultMessage: "Non-covered service",
  },
  pageTitleOutOfNetwork: {
    id: "pageTitleOutOfNetwork",
    defaultMessage: "Out of network",
  },
  pageTitlePriorAuthorization: {
    id: "pageTitlePriorAuthorization",
    defaultMessage: "Prior authorization",
  },
  pageTitleNoLongerAvailable: {
    id: "pageTitleNoLongerAvailable",
    defaultMessage: "No longer available",
  },
  pageTitleInsuranceNoLongerAvailable: {
    id: "pageTitleInsuranceNoLongerAvailable",
    defaultMessage: "Insurance no longer available",
  },
  pageTitleUpsGuestLogin: {
    id: "pageTitleUpsGuestLogin",
    defaultMessage: "Guest login",
  },
  pageTitleUpdatePaymentMethod: {
    id: "pageTitleUpdatePaymentMethod",
    defaultMessage: "Update payment method",
  },
  pageTitleReceipt: { id: "pageTitleReceipt", defaultMessage: "Receipt" },
  pageTitleReviewInvoice: {
    id: "pageTitleReviewInvoice",
    defaultMessage: "Review invoice",
  },
  pageTitleCreatePaymentMethod: {
    id: "pageTitleCreatePaymentMethod",
    defaultMessage: "Create payment method",
  },
  pageTitleUpsPaymentConfirmation: {
    id: "pageTitleUpsPaymentConfirmation",
    defaultMessage: "Payment confirmation",
  },
  pageTitlePaymentFailure: {
    id: "pageTitlePaymentFailure",
    defaultMessage: "Payment failure",
  },
  pageTitlePaymentNotAvailable: {
    id: "pageTitlePaymentNotAvailable",
    defaultMessage: "Payment not available",
  },
});

const messageIdToKey: Record<string, keyof typeof messages> = {
  pageTitleHome: "pageTitleHome",
  pageTitleLogIn: "pageTitleLogIn",
  pageTitleSignUp: "pageTitleSignUp",
  pageTitleActivation: "pageTitleActivation",
  pageTitleResetPassword: "pageTitleResetPassword",
  pageTitleResetPasswordError: "pageTitleResetPasswordError",
  pageTitleSetPassword: "pageTitleSetPassword",
  pageTitleSendEmail: "pageTitleSendEmail",
  pageTitleUnlock: "pageTitleUnlock",
  pageTitleUpdateEmailError: "pageTitleUpdateEmailError",
  pageTitleMyAccount: "pageTitleMyAccount",
  pageTitleConfirmation: "pageTitleConfirmation",
  pageTitleSetupProfile: "pageTitleSetupProfile",
  pageTitleSecuritySettings: "pageTitleSecuritySettings",
  pageTitleChangePassword: "pageTitleChangePassword",
  pageTitleEditMfa: "pageTitleEditMfa",
  pageTitleUpdateEmail: "pageTitleUpdateEmail",
  pageTitleResendUpdateEmail: "pageTitleResendUpdateEmail",
  pageTitleContactDetails: "pageTitleContactDetails",
  pageTitlePersonalInformation: "pageTitlePersonalInformation",
  pageTitleCommunicationPreferences: "pageTitleCommunicationPreferences",
  pageTitleVideo: "pageTitleVideo",
  pageTitleTest: "pageTitleTest",
  pageTitleTestHistory: "pageTitleTestHistory",
  pageTitleSampleDraw: "pageTitleSampleDraw",
  pageTitleSimpleOrder: "pageTitleSimpleOrder",
  pageTitleScheduleBloodDraw: "pageTitleScheduleBloodDraw",
  pageTitleDrawRequest: "pageTitleDrawRequest",
  pageTitleBillingCenter: "pageTitleBillingCenter",
  pageTitleBillingGuestLogin: "pageTitleBillingGuestLogin",
  pageTitlePayment: "pageTitlePayment",
  pageTitleReviewBills: "pageTitleReviewBills",
  pageTitlePayBill: "pageTitlePayBill",
  pageTitleBillingFailure: "pageTitleBillingFailure",
  pageTitlePaymentConfirmation: "pageTitlePaymentConfirmation",
  pageTitlePaymentPlanDetail: "pageTitlePaymentPlanDetail",
  pageTitlePaymentReceipt: "pageTitlePaymentReceipt",
  pageTitlePaymentSuccess: "pageTitlePaymentSuccess",
  pageTitleUpdatePayment: "pageTitleUpdatePayment",
  pageTitleBillingHistory: "pageTitleBillingHistory",
  pageTitleBillingHelp: "pageTitleBillingHelp",
  pageTitleStatements: "pageTitleStatements",
  pageTitlePaymentPlans: "pageTitlePaymentPlans",
  pageTitlePaymentHistory: "pageTitlePaymentHistory",
  pageTitlePatientEducation: "pageTitlePatientEducation",
  pageTitleBillingUpdates: "pageTitleBillingUpdates",
  pageTitleBillingEstimate: "pageTitleBillingEstimate",
  pageTitleGuestLogin: "pageTitleGuestLogin",
  pageTitleNonCoveredService: "pageTitleNonCoveredService",
  pageTitleOutOfNetwork: "pageTitleOutOfNetwork",
  pageTitlePriorAuthorization: "pageTitlePriorAuthorization",
  pageTitleNoLongerAvailable: "pageTitleNoLongerAvailable",
  pageTitleInsuranceNoLongerAvailable: "pageTitleInsuranceNoLongerAvailable",
  pageTitleUpsGuestLogin: "pageTitleUpsGuestLogin",
  pageTitleUpdatePaymentMethod: "pageTitleUpdatePaymentMethod",
  pageTitleReceipt: "pageTitleReceipt",
  pageTitleReviewInvoice: "pageTitleReviewInvoice",
  pageTitleCreatePaymentMethod: "pageTitleCreatePaymentMethod",
  pageTitleUpsPaymentConfirmation: "pageTitleUpsPaymentConfirmation",
  pageTitlePaymentFailure: "pageTitlePaymentFailure",
  pageTitlePaymentNotAvailable: "pageTitlePaymentNotAvailable",
};

const PageTitle: React.FC = () => {
  const { pathname } = useLocation();
  const intl = useIntl();

  useEffect(() => {
    const messageId = getPageTitleMessageId(pathname);
    const messageKey = messageId ? messageIdToKey[messageId] : null;
    const pageName = messageKey
      ? intl.formatMessage(messages[messageKey])
      : intl.formatMessage(messages.pageTitleHome);
    document.title = `${pageName} | ${PAGE_TITLE_SITE_SUFFIX}`;
  }, [pathname, intl]);

  return null;
};

export default PageTitle;
